﻿<!--
  TracingBeam — a thin vertical spine down the left gutter whose filled length
  tracks scroll progress through the wrapped content, with a marker at the head.
  Progressive enhancement: SSR/no-JS shows a static faint line; reduced-motion
  shows the line fully filled (no scroll tracking). No glow — crisp 1px rules.
-->
<template>
  <div ref="root" class="tb">
    <div class="tb-rail" aria-hidden="true">
      <div class="tb-fill" :style="{ height: pct + '%' }"></div>
      <div class="tb-head" :style="{ top: pct + '%' }"></div>
    </div>
    <div class="tb-content">
      <slot />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue';

export default defineComponent({
  name: 'TracingBeam',
  setup() {
    const root = ref<HTMLElement | null>(null);
    const pct = ref(0);
    let raf = 0;
    let onScroll: (() => void) | null = null;

    onMounted(() => {
      const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) { pct.value = 100; return; }

      const update = () => {
        raf = 0;
        const el = root.value;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        // 0% when the block's top reaches mid-viewport, 100% when its bottom does
        const span = r.height - vh * 0.5;
        const progressed = (vh * 0.5) - r.top;
        pct.value = Math.max(0, Math.min(100, span > 0 ? (progressed / span) * 100 : 100));
      };
      onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      update();
    });

    onBeforeUnmount(() => {
      if (onScroll) {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onScroll);
      }
      if (raf) cancelAnimationFrame(raf);
    });

    return { root, pct };
  },
});
</script>

<style scoped>
.tb {
  position: relative;
}
.tb-rail {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 1px;
  background: rgba(var(--lp-fg), 0.08);
  pointer-events: none;
}
.tb-fill {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  background: rgba(var(--jb-primary-rgb), 0.9);
}
.tb-head {
  position: absolute;
  left: -2px;
  width: 5px;
  height: 5px;
  margin-top: -2px;
  border-radius: 9999px;
  background: rgba(var(--jb-primary-rgb), 1);
}
/* On narrow viewports the gutter collapses — hide the rail, keep content flush */
@media (max-width: 1024px) {
  .tb-rail { display: none; }
}
</style>
